import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7 } from "styled-components";
import React from "react";
import { type Mode } from "~/features/ui/get-theme-style";
import { hasItems, takeFirst } from "~/features/app/utils";
import { Media } from "~/features/ui/media";
import { isLinkValid } from "~/features/ui/link/utils";
import { Wrap } from "~/features/ui/wrap";
import { Slider } from "~/features/ui/slider";
import { Progress } from "~/features/ui/slider/progress";
import { GeneralCard } from "./general-card";
const Section = _styled.section.withConfig({
  componentId: "sc-sf-y3tf76-0"
})`
  ${{
  "overflow": "hidden"
}}

  .splide__track {
    overflow: visible !important;
  }
`;
var _StyledSection = _styled2(Section).withConfig({
  componentId: "sc-sf-y3tf76-1"
})`${p => p.$_css}`;
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type GridCardsBlockViewProps<TCard = unknown> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  cards?: TCard[] | null;
  getItemKey?: (card: TCard, index: number) => React.Key;
  hasTopDivider?: boolean | null;
  mode?: string | null;
  renderCard: (card: TCard, index: number) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
};
export function GridCardsBlockView<TCard = unknown>({
  anchor,
  cards = [],
  getItemKey,
  hasTopDivider = true,
  mode = "light",
  renderCard,
  sectionProps
}: GridCardsBlockViewProps<TCard>) {
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);
  const cardList = cards ?? [];
  if (!hasItems(cardList)) {
    return null;
  }
  const items = cardList.map((card, idx) => <React.Fragment key={getItemKey?.(card, idx) || idx}>{renderCard(card, idx)}</React.Fragment>);
  return <_StyledSection {...sectionProps} data-theme={mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, {
    "position": "relative",
    "backgroundColor": "var(--bg-secondary)",
    "color": "var(--text-primary)"
  }]}>
      <Wrap mode={mode as Mode}>
        {/* Desktop: 3-column grid */}
        <_StyledDiv $_css2={[{
        "display": "none",
        "paddingLeft": "var(--spacing-xl)",
        "paddingRight": "var(--spacing-xl)",
        "paddingTop": "var(--spacing-2xl)",
        "paddingBottom": "var(--spacing-2xl)",
        "@media (min-width: 1024px)": {
          "display": "block"
        }
      }]}>
          <_StyledDiv2 $_css3={[{
          "display": "grid",
          "gridTemplateColumns": "repeat(3, minmax(0, 1fr))",
          "gap": "var(--spacing-md)",
          "rowGap": "var(--spacing-2xl)"
        }]}>{items}</_StyledDiv2>
        </_StyledDiv>

        {/* Mobile: slider */}
        <_StyledDiv3 $_css4={[{
        "@media (min-width: 1024px)": {
          "display": "none"
        }
      }]}>
          <_StyledDiv4 $_css5={[{
          "paddingLeft": "var(--spacing-md)",
          "paddingRight": "var(--spacing-md)",
          "paddingBottom": "var(--spacing-xl)",
          "paddingTop": "var(--spacing-md)"
        }]}>
            <Slider onMove={splide => setActiveSlideIndex(splide.Components.Controller.getIndex())} options={{
            fixedWidth: items.length === 1 ? "100%" : "95%",
            gap: 16
          }}>
              {items.map((child, idx) => <Slider.Slide key={idx}>{child}</Slider.Slide>)}
            </Slider>
          </_StyledDiv4>

          {items.length > 1 && <_StyledDiv5 $_css6={[{
          "paddingLeft": "var(--spacing-md)",
          "paddingRight": "var(--spacing-md)",
          "paddingTop": "var(--spacing-lg)",
          "paddingBottom": "var(--spacing-lg)"
        }]}>
              <Progress activeSlideIndex={activeSlideIndex} totalSlides={items.length} />
            </_StyledDiv5>}
        </_StyledDiv3>
      </Wrap>
    </_StyledSection>;
}
var _StyledDiv = _styled3("div").withConfig({
  componentId: "sc-sf-y3tf76-2"
})`${p => p.$_css2}`;
var _StyledDiv2 = _styled4("div").withConfig({
  componentId: "sc-sf-y3tf76-3"
})`${p => p.$_css3}`;
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-y3tf76-4"
})`${p => p.$_css4}`;
var _StyledDiv4 = _styled6("div").withConfig({
  componentId: "sc-sf-y3tf76-5"
})`${p => p.$_css5}`;
var _StyledDiv5 = /*#__PURE__*/_styled7("div").withConfig({
  displayName: "grid-cards___StyledDiv5",
  componentId: "sc-p3fkk8-0"
})(["", ""], p => p.$_css6);